/* EKNO cart drawer & cart page — AJAX Cart API with Section Rendering. */
(function () {
  'use strict';
  const EKNO = window.EKNO;
  const utils = () => EKNO.utils;

  class EknoCart extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId || 'cart-drawer';
      this.isDrawer = this.tagName === 'EKNO-CART-DRAWER';
      this.addEventListener('change', (e) => {
        const qty = e.target.closest('[data-cart-qty]');
        if (qty) this.change(qty.dataset.line, parseInt(qty.value, 10));
        const note = e.target.closest('[data-cart-note]');
        if (note) this.updateNote(note.value);
      });
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-cart-remove]');
        if (rm) { e.preventDefault(); this.change(rm.dataset.line, 0); }
      });
      document.addEventListener('ekno:cart:added', (e) => this.onAdded(e.detail));
      this.updateFreeShipping();
      this.loadRecommendations();
    }
    onAdded(detail) {
      if (detail?.sections?.[this.sectionId]) this.render(detail.sections[this.sectionId]);
      else this.refresh();
      if (this.isDrawer && EKNO.cartType === 'drawer') this.open();
    }
    async refresh() {
      try {
        const res = await fetch(`${EKNO.routes.cart}?sections=${this.sectionId}`);
        const data = await res.json();
        if (data[this.sectionId]) this.render(data[this.sectionId]);
      } catch (e) { /* silent */ }
    }
    async change(line, quantity) {
      this.setLoading(line, true);
      try {
        const res = await fetch(EKNO.routes.cartChange + '.js', Object.assign(utils().fetchConfig(), { body: JSON.stringify({ line: Number(line), quantity, sections: this.sectionId }) }));
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || EKNO.strings.cartError);
        if (data.sections?.[this.sectionId]) this.render(data.sections[this.sectionId]);
        else await this.refresh();
        this.updateCount(data.item_count);
        document.dispatchEvent(new CustomEvent('ekno:cart:updated', { detail: data }));
      } catch (err) {
        this.showError(err.message);
      } finally {
        this.setLoading(line, false);
      }
    }
    async updateNote(note) {
      try { await fetch(EKNO.routes.cartUpdate + '.js', Object.assign(utils().fetchConfig(), { body: JSON.stringify({ note }) })); } catch (e) {}
    }
    render(html) {
      const fresh = utils().sectionHTML(html, `[data-cart-body="${this.sectionId}"]`);
      const current = this.querySelector(`[data-cart-body="${this.sectionId}"]`);
      if (fresh && current) {
        current.replaceWith(fresh);
        const count = Number(fresh.dataset.itemCount || 0);
        this.updateCount(count);
        this.updateFreeShipping();
        this.loadRecommendations();
        document.dispatchEvent(new CustomEvent('ekno:dom:updated', { detail: { root: fresh } }));
      }
    }
    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = count === 0; });
      document.querySelectorAll('[data-cart-count-label]').forEach((el) => { el.textContent = `${count}`; });
    }
    updateFreeShipping() {
      const bar = this.querySelector('[data-free-shipping]');
      if (!bar) return;
      const threshold = Number(bar.dataset.threshold || 0);
      const total = Number(bar.dataset.total || 0);
      if (!threshold) { bar.hidden = true; return; }
      const remaining = Math.max(0, threshold - total);
      const pct = Math.min(100, Math.round((total / threshold) * 100));
      bar.querySelector('[data-free-shipping-fill]').style.width = pct + '%';
      const msg = bar.querySelector('[data-free-shipping-text]');
      msg.innerHTML = remaining > 0 ? EKNO.strings.freeShippingRemaining.replace('[amount]', `<strong>${utils().formatMoney(remaining)}</strong>`) : EKNO.strings.freeShippingReached;
      bar.classList.toggle('is-reached', remaining === 0);
      bar.hidden = false;
    }
    setLoading(line, on) {
      const item = this.querySelector(`[data-line="${line}"]`);
      if (item) { item.classList.toggle('is-loading', on); item.setAttribute('aria-busy', on ? 'true' : 'false'); }
    }
    showError(msg) {
      const box = this.querySelector('[data-cart-errors]');
      if (box) { box.textContent = msg; box.hidden = false; setTimeout(() => { box.hidden = true; }, 5000); }
      else utils().toast(msg);
    }
    async loadRecommendations() {
      const holder = this.querySelector('[data-cart-recommendations]');
      if (!holder) return;
      const productId = holder.dataset.productId;
      if (!productId) { holder.hidden = true; return; }
      if (holder.dataset.loadedFor === productId) return;
      holder.dataset.loadedFor = productId;
      const tryIntent = async (intent) => {
        const res = await fetch(`${EKNO.routes.productRecommendations}?section_id=cart-recommendations&product_id=${productId}&limit=4&intent=${intent}`);
        if (!res.ok) return null;
        const html = await res.text();
        const list = utils().sectionHTML(html, '[data-recommendations-list]');
        return list && list.children.length ? list : null;
      };
      try {
        const list = (await tryIntent('complementary')) || (await tryIntent('related'));
        if (list) { holder.querySelector('[data-cart-recommendations-list]').replaceChildren(...list.children); holder.hidden = false; document.dispatchEvent(new CustomEvent('ekno:dom:updated', { detail: { root: holder } })); }
        else holder.hidden = true;
      } catch (e) { holder.hidden = true; }
    }
  }

  class EknoCartDrawer extends EknoCart {
    connectedCallback() {
      super.connectedCallback();
      this.drawer = this.closest('ekno-drawer') || this.querySelector('ekno-drawer');
    }
    open(opener) { this.drawer?.open(opener); }
    close() { this.drawer?.close(); }
  }

  customElements.define('ekno-cart', EknoCart);
  customElements.define('ekno-cart-drawer', EknoCartDrawer);
})();
